/**
 * Invoice formatting helpers — pure (no DOM) so they can be exercised directly
 * with `node apps/web/src/lib/invoice.check.ts` and shared by both transaction
 * pages' invoice views.
 */

/** The slice of a payment row an invoice needs; admin rows add `tenantName`. */
export interface InvoicePayment {
  id: string;
  planName: string | null;
  billingCycle: string | null;
  streamName: string | null;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  providerRef: string | null;
  method: string | null;
  paidAt: string | null;
  note: string | null;
  receipt: string | null;
  createdAt: string;
  /** Admin view only — who paid. */
  tenantName?: string;
}

/** ₹1,499 / ₹1,499.50 — Indian digit grouping, 0–2 fraction digits. */
export function money(n: number, currency = 'INR'): string {
  return n.toLocaleString('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * ISO → `1 September 2026`. The date parts are read from the string (not via
 * `Date` getters) so a UTC-midnight timestamp never slips a day in a west-of-UTC
 * timezone.
 */
export function longDate(iso: string | null): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    }
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Human-stable invoice number: `INV-2026-RCP9` — receipt first (finance already
 * files by it), else gateway reference, else the payment id, so every row can
 * be re-found from a printed copy.
 */
export function invoiceNumber(r: InvoicePayment): string {
  const seed = (r.receipt || r.providerRef || r.id).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  const iso = r.paidAt ?? r.createdAt;
  const year = /^\d{4}/.exec(iso)?.[0] ?? '';
  return `INV-${year}-${seed.slice(-8) || r.id.slice(0, 8).toUpperCase()}`;
}
